import React, { useState, FC, useEffect } from 'react'
import { Field } from 'formik'
import GoogleClient from '@mortgage-pos/ui/services/GoogleClient'
import { fullStoryBlockList } from '@mortgage-pos/ui/utils/fullStoryBlockList'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import InfoBlock from '@mortgage-pos/ui/components/InfoBlock'
import { ManualFields } from './ManualFields'
import { AutoCompleteField } from './AutoCompleteField'
import { useAddressFieldsAutoComplete } from './useAddressFieldsAutoComplete'
import {
  isCompleteAddress,
  addressFields,
  getQuestionDisplayName,
} from './helpers'

type AutoCompleteFillAddress = {
  autoCompleteAddressFields: string
  street: string
  apt: string
  state: string
  city: string
  zipCode: string
}

export interface Props {
  name: string
  title?: string
  modifierClass: string
  subTitle?: string
  autoCompleteFillAddressValues?: AutoCompleteFillAddress
  setValue: (value: string) => void
}
export const AddressFieldsAutoComplete: FC<Props> = ({
  name,
  title,
  modifierClass,
  subTitle,
  autoCompleteFillAddressValues = {},
  setValue,
}) => {
  const { fieldInputted, elementClicked } = useStoreActions(
    (state) => state.tracking
  )
  const { userLocation } = useStoreState((state) => state.address)
  const { isPrePricingFlow } = useStoreState((state) => state.questionnaire)

  const { inputText, setInputText, results } =
    useAddressFieldsAutoComplete(userLocation)

  const [enterAddressManually, setEnterAddressManually] = useState(false)
  const [fullAddressNotFound, setFullAddressNotFound] = useState(false)

  useEffect(() => {
    const { street, autoCompleteAddressFields } = autoCompleteFillAddressValues

    // Show all address fields with prefilled answers if no google autocomplete address stored
    if (!autoCompleteAddressFields && street) {
      setEnterAddressManually(true)
    }
  }, [autoCompleteFillAddressValues])

  const handleInputChange = async (e, setFieldValue) => {
    const { value } = e.target
    setValue(value)
    setInputText(value)
    setFieldValue(value)
    if (isCompleteAddress(value, results)) {
      try {
        const address = await GoogleClient.getAddressDetails(value)

        addressFields.forEach((field) => {
          setFieldValue(field, address[field] || '')
        })

        // If any address value is missing from geocoding, show the full address fields
        addressFields.forEach((field) => {
          if (field !== 'apt' && !address[field]) {
            setEnterAddressManually(true)
            setFullAddressNotFound(true)
            fieldInputted({
              questionName: 'newAutoCompleteAddressComponent',
              value: 'Full address not found',
              subTitle: subTitle,
            })
            return
          }
        })
      } catch (e) {
        setEnterAddressManually(true)
        setFullAddressNotFound(true)
      }
    }
  }
  const shouldBlockFromFullstory = fullStoryBlockList.includes(name)
  const aptQuestionName = 'apt'

  return (
    <div
      className={`${
        shouldBlockFromFullstory ? 'fs-exclude' : ''
      } address-fields-auto-complete ${modifierClass || ''}`}
    >
      {title ? (
        <h2 className="address-fields-auto-complete__title">{title}</h2>
      ) : null}
      {fullAddressNotFound ? (
        <h3 className="address-fields-auto-complete__address-not-found">
          We had trouble validating your address, please enter here.
        </h3>
      ) : null}
      {enterAddressManually ? (
        <ManualFields addressFields={addressFields} />
      ) : (
        <>
          <AutoCompleteField
            addressFields={addressFields}
            name={name}
            handleInputChange={handleInputChange}
            results={results}
            inputText={inputText}
            setInputText={setInputText}
            setEnterAddressManually={setEnterAddressManually}
            elementClicked={elementClicked}
          />
          {isPrePricingFlow ? (
            <Field name={aptQuestionName} key={aptQuestionName}>
              {({
                field: { onChange: formikChange },
                meta: { value, error, touched },
              }) => {
                const errorClass = error && touched ? '--error' : ''

                return (
                  <div
                    className={`input ${errorClass} --narrow`}
                    key={aptQuestionName}
                  >
                    <label
                      className="input__subtitle"
                      htmlFor={aptQuestionName}
                    >
                      {getQuestionDisplayName(aptQuestionName)}
                    </label>
                    <input
                      name={aptQuestionName}
                      className="input__field"
                      data-testid={aptQuestionName}
                      onChange={(event) => {
                        formikChange(event)
                      }}
                      value={value}
                      placeholder={aptQuestionName}
                      title={aptQuestionName}
                    />
                    {error && touched ? (
                      <InfoBlock type="danger">{error}</InfoBlock>
                    ) : null}
                  </div>
                )
              }}
            </Field>
          ) : null}
        </>
      )}
    </div>
  )
}

export default AddressFieldsAutoComplete
